import { isString } from "lodash";
import { PureComponent, ReactNode } from "react";

import { Link } from "@livechat/design-system-react-components";

import { CustomerDetailsTooltip } from "components/customer-details";

import { Container, EmptyValue, StyledImage, StyledLabel, StyledLabelValue, Value } from "./LabelStyles";
import { LinkEllipsisContainer, LinkEllipsisStyles, isValidUrl } from "./Link";

interface IProps {
  inline?: boolean;
  placeholder?: string;
  label?: ReactNode | string;
  value?: ReactNode | string;
  title?: string;
  imageUrl?: string;
  onLinkClick?(): void;
}

interface ILabelWithLinkProps {
  label?: ReactNode | string;
  value?: ReactNode | string;
  inline?: boolean;
  height?: string;
  title?: string;
  imageUrl?: string;
  onLinkClick?(): void;
}

interface ILabelWithLinkState {
  isVisible: boolean;
}

class LabelWithLink extends PureComponent<ILabelWithLinkProps, ILabelWithLinkState> {
  state = { isVisible: false };

  handleMouseLeave = (): void => {
    this.setState({ isVisible: false });
  };

  handleMouseEnter = (): void => {
    this.setState({ isVisible: true });
  };

  private renderLink = (): ReactNode => (
    <Link
      bold={false}
      href={this.props.value as string}
      rel="noopener noreferrer"
      onClick={this.props.onLinkClick}
      css={LinkEllipsisStyles}
      onMouseEnter={this.handleMouseEnter}
      onMouseLeave={this.handleMouseLeave}
    >
      {this.props.value}
    </Link>
  );

  render(): JSX.Element {
    return (
      <CustomerDetailsTooltip isVisible={this.state.isVisible} tooltipContent={this.props.value}>
        <div css={LinkEllipsisContainer}>
          <StyledLabelValue>
            {this.props.imageUrl && <StyledImage src={this.props.imageUrl} alt="header" />}
            {this.props.label && (
              <StyledLabel inline={Boolean(this.props.inline)}>
                {this.props.label}
                &nbsp;
              </StyledLabel>
            )}
            {this.renderLink()}
          </StyledLabelValue>
        </div>
      </CustomerDetailsTooltip>
    );
  }
}

interface IValueProps {
  hasSpaces: boolean;
  title?: string;
}

interface ILabelWithValueProps {
  inline?: boolean;
  placeholder?: string;
  label?: ReactNode | string;
  value?: ReactNode | string;
  title?: string;
  imageUrl?: string;
}

class LabelWithValue extends PureComponent<ILabelWithValueProps> {
  render(): JSX.Element {
    const { inline, placeholder, label, value, title } = this.props;

    const valueProps: IValueProps = {
      hasSpaces: true,
    };

    if (isString(value)) {
      valueProps.hasSpaces = /\s/.test(value as string);
    }

    if (title) {
      valueProps.title = title;
    }

    return (
      <Container>
        <StyledLabelValue>
          {this.props.imageUrl && <StyledImage src={this.props.imageUrl} alt="header" />}
          {label && (
            <StyledLabel inline={!!inline}>
              {label}
              &nbsp;
            </StyledLabel>
          )}
          {value && <Value {...valueProps}>{value}</Value>}
          {!value && <EmptyValue>{placeholder}</EmptyValue>}
        </StyledLabelValue>
      </Container>
    );
  }
}

export class Label extends PureComponent<IProps> {
  render(): JSX.Element {
    const { inline = true, ...rest } = this.props;
    const isValueAValidUrl = isString(this.props.value) && isValidUrl(this.props.value as string);
    const shouldRenderLink = isValueAValidUrl;

    if (shouldRenderLink) {
      return <LabelWithLink {...rest} inline={inline} />;
    }
    return <LabelWithValue {...rest} inline={inline} />;
  }
}
